import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Badge,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  FullscreenSpinner,
  type Column,
} from '@shared/ui';
import { productApi, type ProductListItem } from '@entities/product';
import { formatDateTime, formatMoney } from '@shared/lib/format';
import { pickLocalized, useCurrentLocale } from '@shared/i18n';
import { ROUTES } from '@shared/config/routes';
export { ProductDetailPage } from './ui/product-detail-page';

export function CatalogListPage() {
  const locale = useCurrentLocale();
  const { data, isLoading } = useQuery({
    queryKey: ['products', 'moderation-queue'],
    queryFn: () => productApi.moderationQueue({ size: 50 }),
  });

  const columns = useMemo<Column<ProductListItem>[]>(
    () => [
      {
        key: 'name',
        header: 'Product',
        cell: (r) => (
          <Link
            to={ROUTES.productDetail(r.id)}
            className="font-medium text-primary hover:underline"
          >
            {pickLocalized(r.name, locale, '—')}
          </Link>
        ),
      },
      {
        key: 'manufacturer',
        header: 'Manufacturer',
        cell: (r) => (
          <Link
            to={ROUTES.manufacturerDetail(r.manufacturer.id)}
            className="flex items-center gap-2 text-xs hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate font-medium">{r.manufacturer.companyName}</span>
            <Badge variant="outline" className="text-[10px]">
              {r.manufacturer.kycStatus}
            </Badge>
          </Link>
        ),
      },
      { key: 'country', header: 'Country', cell: (r) => r.originCountry },
      {
        key: 'hs',
        header: 'HS',
        cell: (r) => (r.hsCode ? <span className="font-mono text-xs">{r.hsCode}</span> : '—'),
      },
      { key: 'moq', header: 'MOQ', cell: (r) => r.moq ?? '—' },
      {
        key: 'price',
        header: 'Export price',
        cell: (r) => formatMoney(r.exportPrice),
      },
      {
        key: 'flags',
        header: '',
        cell: (r) => (
          <div className="flex gap-1">
            {r.stock ? (
              <Badge variant="secondary">
                stock{r.stockDiscountPct ? ` −${r.stockDiscountPct}%` : ''}
              </Badge>
            ) : null}
            {r.eco ? <Badge variant="success">eco</Badge> : null}
          </div>
        ),
      },
      { key: 'created', header: 'Created', cell: (r) => formatDateTime(r.createdAt) },
    ],
    [locale],
  );

  if (isLoading) return <FullscreenSpinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Очередь модерации товаров</h1>
      <Card>
        <CardContent className="p-0">
          <DataTable
            rows={data?.content ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            empty={<EmptyState title="Нет товаров на модерации" />}
          />
        </CardContent>
      </Card>
    </div>
  );
}
